import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, LogOut, User } from "lucide-react";
import { MessageCircle } from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";
import { useAuth } from "@/contexts/AuthContext";

const Header = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { user, profile, signOut, loading: authLoading, initialized } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const displayName = profile?.username || user?.email?.split('@')[0] || 'User';

  const navItems = [
    { label: t('nav.home'), href: "/" },
    { label: t('nav.features'), href: "/features" },
    { label: "Pricing", href: "/pricing" },
    { label: t('nav.community'), href: "/community" },
    { label: "Upcoming", href: "/roadmap" },
    { label: "Our CEX partner", href: "/bitget" },
    { label: t('nav.faq'), href: "/faq" },
    { label: "Dashboard v1.0", href: "https://dashboard.oraculusx.com", external: true },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || isMobileMenuOpen
          ? "header-gradient-animated backdrop-blur-xl border-b border-border shadow-lg"
          : "bg-transparent"
      }`}
    >
      <div className="container px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link
            to="/"
            className="text-xl md:text-2xl font-bold text-white hover:opacity-80 transition-opacity"
          >
            Oraculus<span className="gradient-x-animated">:X</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              item.label === "Dashboard v1.0" ? (
                <Button
                  key={item.href}
                  asChild
                  variant="secondary"
                  size="sm"
                  className="bg-gradient-primary hover:shadow-glow-primary transition-all duration-300"
                >
                  <a href={item.href} target="_blank" rel="noopener noreferrer">
                    {item.label}
                  </a>
                </Button>
              ) : (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`px-4 py-2 text-sm font-semibold transition-colors relative group ${
                    location.pathname === item.href 
                      ? "text-white" 
                      : "text-white/90 hover:text-white"
                  }`}
                >
                  {item.label}
                  <span className={`absolute bottom-0 left-0 h-0.5 bg-gradient-primary transition-all duration-300 ${
                    location.pathname === item.href ? "w-full" : "w-0 group-hover:w-full"
                  }`} />
                </Link>
              )
            ))}
          </nav>

          {/* Social Icons, Language Switcher & Auth Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="https://discord.gg/fxXR3h8b9k"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 hover:bg-secondary/50 rounded-lg transition-colors group"
              aria-label="Discord"
            >
              <MessageCircle className="w-5 h-5 text-white/80 group-hover:text-white transition-colors" />
            </a>
            <LanguageSwitcher />
            {authLoading ? (
              <div className="w-20 h-9 bg-primary/10 animate-pulse rounded-lg" />
            ) : user ? (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 px-3 py-2 bg-primary/10 rounded-lg border border-primary/20">
                  <User className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">{displayName}</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={signOut}
                  className="border-primary/50 hover:bg-primary/10"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-primary/50 hover:bg-primary/10"
                  asChild
                >
                  <Link to="/auth?mode=login">Log In</Link>
                </Button>
                <Button
                  size="sm"
                  className="bg-gradient-primary hover:shadow-glow-primary animate-glow"
                  asChild
                >
                  <Link to="/auth?mode=signup">Sign Up</Link>
                </Button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 hover:bg-secondary/50 rounded-lg transition-colors"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="lg:hidden py-4 space-y-2 animate-fade-in backdrop-blur-xl border-t border-border">
            {navItems.map((item) => (
              item.label === "Dashboard v1.0" ? (
                <Button
                  key={item.href}
                  asChild
                  className="w-full bg-gradient-primary hover:shadow-glow-primary transition-all duration-300"
                >
                  <a href={item.href} target="_blank" rel="noopener noreferrer">
                    {item.label}
                  </a>
                </Button>
              ) : (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`block w-full text-left px-4 py-3 hover:text-white hover:bg-secondary/50 rounded-lg transition-colors font-semibold ${
                    location.pathname === item.href 
                      ? "text-white bg-secondary/30" 
                      : "text-white/90"
                  }`}
                >
                  {item.label}
                </Link>
              )
            ))}
            <div className="px-4 pt-2 flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <a
                  href="https://discord.gg/fxXR3h8b9k"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 hover:bg-secondary/50 rounded-lg transition-colors flex items-center gap-2"
                  aria-label="Discord"
                >
                  <MessageCircle className="w-5 h-5 text-white/80" />
                  <span className="text-sm text-white/80">Discord</span>
                </a>
              </div>
              <LanguageSwitcher />
              {authLoading ? (
                <div className="w-full h-20 bg-primary/10 animate-pulse rounded-lg mt-4" />
              ) : user ? (
                <div className="flex flex-col gap-3 pt-4 border-t border-border">
                  <div className="flex items-center gap-2 px-3 py-2 bg-primary/10 rounded-lg border border-primary/20">
                    <User className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium">{displayName}</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={signOut}
                    className="w-full border-primary/50 hover:bg-primary/10"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col gap-2 pt-4 border-t border-border">
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full border-primary/50 hover:bg-primary/10"
                    asChild
                  >
                    <Link to="/auth?mode=login">Log In</Link>
                  </Button>
                  <Button
                    size="lg"
                    className="w-full bg-gradient-primary hover:shadow-glow-primary"
                    asChild
                  >
                    <Link to="/auth?mode=signup">Sign Up</Link>
                  </Button>
                </div>
              )}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
